import React, { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react';
import {
	FormImageUploaderNewContext,
	IFormImageUploaderNewContext,
	IMaxFileSizeMB,
} from '../../formImageUploaderNewState/formImageUploaderNewContext';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Radio, RadioChangeEvent } from 'antd';
import { openBrowseFile } from 'utils/openBrowseFile';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { Icon, IconName } from 'components/Icon/Icon';
import { NewButton } from 'components/Button/NewButton/NewButton';
import Text from 'components/Text/Text';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import style from './MediaModal.module.scss';

export interface IImageTabPaneHandle {
	apply: () => void;
}

interface IProps {
	horizontalAspectRatio?: number;
	verticalAspectRatio?: number;
	maxWidth?: number;
	maxHeight?: number;
	maxFileSizeMB: IMaxFileSizeMB;
	noImageIconId: string;
	loading: boolean;
	setIsVideoActive?: (value: boolean) => void;
}

export const ImageTabPane = forwardRef<IImageTabPaneHandle, IProps>(
	(
		{
			horizontalAspectRatio,
			verticalAspectRatio,
			maxWidth,
			maxHeight,
			maxFileSizeMB,
			noImageIconId,
			loading,
			setIsVideoActive,
		},
		ref,
	) => {
		const {
			pendingImage,
			setPendingImage,
			changeTypeOfErrorModalWindow,
			imageOrientation,
			setImageOrientation,
			uploadImage,
			keepExistingFile,
		} = useContext<IFormImageUploaderNewContext>(FormImageUploaderNewContext);
		const [crop, setCrop] = useState<Crop | undefined>(undefined);
		const [isSentOriginalImg, setSentOriginalImg] = useState<boolean>(true);
		const [loadingImageErrorInModal, setLoadingImageErrorInModal] = useState<boolean>(false);
		const canvasRef = useRef<HTMLCanvasElement | null>(null);
		const imageRef = useRef<HTMLImageElement | null>(null);

		const aspectRatio = imageOrientation === 'horizontal' ? horizontalAspectRatio : verticalAspectRatio;

		const handleImageChange = (files: FileList | null) => {
			setLoadingImageErrorInModal(false);
			setSentOriginalImg(true);
			setCrop(undefined);
			const imgFile = files ? files[0] : '';
			if (!imgFile) {
				return;
			} else if (imgFile.size > 1000 * 1000 * maxFileSizeMB.img) {
				changeTypeOfErrorModalWindow('fileSizeExceeded');
			} else if (imgFile.type === 'image/jpeg' || imgFile.type === 'image/png') {
				setPendingImage({ file: imgFile, src: URL.createObjectURL(imgFile) });
			} else {
				changeTypeOfErrorModalWindow('invalidFileFormat');
			}
		};

		const handleUploadImage = () => {
			openBrowseFile({
				onChoose: handleImageChange,
				accept: '.jpg, .jpeg, .png',
			});
		};

		const drawImageInCanvas = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
			if (canvasRef.current && imageRef.current) {
				canvasRef.current.width = imageRef.current.naturalWidth;
				canvasRef.current.height = imageRef.current.naturalHeight;

				const context = canvasRef.current.getContext('2d');
				context?.drawImage(e.currentTarget, 0, 0, canvasRef.current.width, canvasRef.current.height);
			}
		};

		const handleCropAreaChange = (c: PixelCrop) => {
			setCrop(c);
			isSentOriginalImg && setSentOriginalImg(false);
			if (canvasRef.current && imageRef.current) {
				canvasRef.current.width = c.width;
				canvasRef.current.height = c.height;
				const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
				const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

				const context = canvasRef.current.getContext('2d');
				context?.drawImage(
					imageRef.current,
					c.x * scaleX,
					c.y * scaleY,
					c.width * scaleX,
					c.height * scaleY,
					0,
					0,
					c.width,
					c.height,
				);
			}
		};

		const showErrorImage = () => {
			setLoadingImageErrorInModal(true);
		};

		const handleOrientationChange = (e: RadioChangeEvent) => {
			setImageOrientation(e.target.value);
		};

		useImperativeHandle(ref, () => ({
			apply: () => {
				setIsVideoActive?.(false);
				const file = pendingImage.file;
				const canvas = canvasRef.current;
				const hasCropSelection = !isSentOriginalImg && !!canvas && canvas.width > 0 && canvas.height > 0;
				if (hasCropSelection) {
					canvas.toBlob(
						(blob) => {
							if (blob) {
								uploadImage(blob);
							}
						},
						'image/jpeg',
						0.8,
					);
					return;
				}
				if (!file) {
					keepExistingFile();
					return;
				}
				uploadImage(file);
			},
		}));

		const renderImage = () => {
			if (loading) {
				return (
					<div className={style.loaderContainer}>
						<LoaderWithProps loaderSize="big" />
					</div>
				);
			}

			if (!pendingImage.src) {
				return (
					<div className={style.noImageContainer}>
						<svg className={style.noImageIcon}>
							<use xlinkHref={`${breadcrumbs}#${noImageIconId}`} />
						</svg>
					</div>
				);
			}

			if (loadingImageErrorInModal) {
				return (
					<div className={style.brokenImageContainer}>
						<svg className={style.brokenImageIcon}>
							<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
						</svg>
						<div>Error image</div>
					</div>
				);
			}

			return (
				<ReactCrop
					className={style.imageContainer}
					crop={crop}
					aspect={aspectRatio}
					maxWidth={maxWidth}
					maxHeight={maxHeight}
					onChange={handleCropAreaChange}>
					<img
						ref={imageRef}
						src={pendingImage.src}
						onLoad={drawImageInCanvas}
						onError={showErrorImage}
						alt="Photo"
					/>
				</ReactCrop>
			);
		};

		return (
			<div className={style.tabPaneBody}>
				{renderImage()}
				{!loading && (
					<div className={style.tabButtons}>
						<div data-test="uploadImageButton">
							<NewButton
								variant="primary"
								color="purple"
								size="small"
								iconLeft={<Icon name={IconName.upload} />}
								onClick={handleUploadImage}>
								<Text
									translationNameSpace="form"
									translationKey="uploadImageButton"
									asFragment={true}
								/>
							</NewButton>
						</div>
						{pendingImage.src && (
							<Radio.Group
								className={style.orientationSwitch}
								value={imageOrientation}
								onChange={handleOrientationChange}>
								<Radio value="horizontal">
									<Text translationNameSpace="form" translationKey="horizontalCropLabel" size="s" />
								</Radio>
								<Radio value="vertical">
									<Text translationNameSpace="form" translationKey="verticalCropLabel" size="s" />
								</Radio>
							</Radio.Group>
						)}
					</div>
				)}
				<canvas className={style.canvas} ref={canvasRef} />
			</div>
		);
	},
);
